import React, { useEffect, useState } from 'react';
import { ContextHolder, AdminPortal } from '@frontegg/react'
import { useNavigate } from 'react-router-dom';
import loadingImg from './loading.svg';
import './App.css';
import { Tenant, TenantMetadata } from './CustomTypes';
import WatchList from './WatchList';
import { useAuthCheck } from './hooks/useAuthCheck';

// Define the mapping between dropdown items and metadata properties
const itemMetadataMap = {
  'Salesforce': 'salesforceSettingsId',
  'BlackDiamond': 'blackDiamondSettingsId',
  'MoneyGuidePro': 'moneyGuideProSettingsId',
  'Adhesion': 'adhesionSettingsId',
  'Stratifi': 'stratifiSettingsId',
  'Box': 'boxSettingsId'
};

function App() {
  const { isLoading, user, tenants } = useAuthCheck();
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_TENANT_BASE_URL || '';
  const organization = window.location.host
    .replace(baseUrl, '')
    .replace('.', '') || 'auth-portal';
  const tenantBySubdomain = tenants.find(tenant => tenant.alias === organization);
  const tenantMetadata: TenantMetadata | null = tenantBySubdomain?.metadata ? JSON.parse(tenantBySubdomain?.metadata) as TenantMetadata : null;
  const userRoles = user?.tenants.find(tenant => tenant.tenantId ===  tenants.find(tenant => tenant.alias === organization)?.tenantId)?.roles;
  const userTenantIds = user?.tenants.map(tenant => tenant.tenantId);
  
  const logout = () => {
    const baseUrl = ContextHolder.getContext().baseUrl;
    window.location.href = `${baseUrl}/oauth/logout?post_logout_redirect_uri=${window.location.origin}`;
   };

  const handleSettingsClick = () => {
    AdminPortal.show();
  };

  const [authError, setAuthError] = useState<string | null>(null);

  const handleItemClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(url, '_blank');
  };

  // Function to check if a dropdown item should be displayed for a tenant
  const shouldDisplayItem = (org: Tenant, itemName: string) => {
    const metadata = org.metadata ? JSON.parse(org.metadata) as TenantMetadata : null;
    const requiredProperty = itemMetadataMap[itemName as keyof typeof itemMetadataMap];
    return metadata && metadata[requiredProperty as keyof TenantMetadata];
  };

  useEffect(() => {
    if (userRoles && !userRoles?.some(role => role.key === 'Admin')) {
      if (!tenantBySubdomain?.name) {
        setAuthError(`You don't have access to this tenant.`);
        return;
      }        
      console.log(`Redirecting to /sso-salesforce. User not in Admin role for ${tenantBySubdomain?.name} tenant.`);
      navigate("/sso-salesforce", { replace: true });
    }
  }, [navigate, tenantBySubdomain?.name, userRoles]);
  
  if (authError) {
    return (
      <div className="App">
        <header className="App-header">
          <h1>Authentication Error</h1>
          <p>{authError}</p>
        </header>
      </div>
    );
  }

  if (isLoading) {
    return <div><img src={loadingImg} alt="loading" width={200} height={200} /></div>;
  }

  return (
    <div className="App">
      <header className="App-header">
        <div className="tenant-logo-container">
          <img 
            src={`${window.location.protocol}//${tenantBySubdomain?.alias}.${baseUrl}/${tenantMetadata?.image}`}
            alt={tenantBySubdomain?.name} 
            className="tenant-logo"
          />
        </div>
        <img src={user?.profilePictureUrl || undefined} alt={user?.name} height={150} />
        <div className="button-container">
          <button type='submit' onClick={handleSettingsClick}>Profile Settings</button>
          <button type='button' onClick={() => console.log(user?.accessToken)}>What is my access token?</button>
          <button type='submit' onClick={() => logout()}>Click to logout</button>
        </div>
        Hello, {user?.name}! Welcome to {tenantBySubdomain?.name} authentication portal.
        <br />
        <br />
        <div className="org-list">
          <div className="org-grid">
            {tenants
              .filter(org => userTenantIds?.includes(org.tenantId ?? ''))
              .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
              .map((org) => (
                <div key={org.alias} className="org-item-container">
                  <div className="org-item">
                    <img 
                      src={`${window.location.protocol}//${org.alias}.${baseUrl}/${(org.metadata ? JSON.parse(org.metadata) as TenantMetadata : null)?.image}`} 
                      alt={`${org.name} logo`} 
                      className="org-logo" 
                    />
                    <span className="org-name">{org.name}</span>
                    <div className="dropdown-items">
                      {Object.entries(itemMetadataMap).map(([itemName, metadataProperty]) => (
                        shouldDisplayItem(org, itemName) && (
                          <a 
                            key={itemName}
                            href={`${window.location.protocol}//${org.alias}.${baseUrl}/sso-${itemName.toLowerCase()}`}
                            className="dropdown-item"
                            onClick={(e) => handleItemClick(e, `${window.location.protocol}//${org.alias}.${baseUrl}/sso-${itemName.toLowerCase()}`)}
                          >
                            {itemName}
                          </a>
                        )
                      ))}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
        <br />
        <div>
          <WatchList />
        </div>
      </header>
    </div>
  );
}

export default App;
